import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Modal, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiEdit, FiImage, FiSave, FiPackage, FiSearch, 
  FiTag, FiPlus, FiX, FiCheck, FiBarChart2, FiLink
} from 'react-icons/fi';

export default function Products() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [saveLoading, setSaveLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ text: '', type: '' });
  const [newProduct, setNewProduct] = useState({
    productId: "",
    productName: "",
    productDescription: "",
    productExtraData: "",
    imageUrl: "",
    status: "Active",
    sku: 0,
    productPrice: 0,
    width: 0,
    height: 0,
    depth: 0,
    quantityAvailable: 0,
    labels: "",
    productImagesUrls: [],
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const adminToken = Cookies.get("adminKey");
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}products/`, {
          method: "GET",
          headers: {
            "Admin-Token": adminToken,
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const formattedRows = data.map((product) => ({
          id: product.productId,
          productName: product.productName,
          productDescription: product.productDescription,
          productExtraData: product.productExtraData,
          imageUrl: product.imageUrl,
          status: product.status,
          sku: product.sku,
          productPrice: product.productPrice,
          width: product.width,
          height: product.height,
          depth: product.depth,
          quantityAvailable: product.quantityAvailable,
          labels: product.labels.join(", "),
          productImagesUrls: product.productImagesUrls || [],
          productCategory: product.productCategory?.categoryName || "N/A",
        }));

        setRows(formattedRows);
        setFilteredRows(formattedRows);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Filter products based on search term and category
  useEffect(() => {
    let result = [...rows];
    
    // Filter by search term
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(product => 
        product.productName.toLowerCase().includes(lowercasedTerm) ||
        product.productDescription.toLowerCase().includes(lowercasedTerm) ||
        product.sku.toString().includes(lowercasedTerm) ||
        product.labels.toLowerCase().includes(lowercasedTerm)
      );
    }
    
    // Filter by category
    if (filterCategory !== 'all') {
      result = result.filter(product => product.productCategory.toLowerCase() === filterCategory.toLowerCase());
    }
    
    setFilteredRows(result);
  }, [searchTerm, filterCategory, rows]);

  const handleEditImagesClick = () => {
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
  };

  const handleImageChange = (index, event) => {
    const newUrls = [...imageUrls];
    newUrls[index] = event.target.value;
    setImageUrls(newUrls);
  };

  const handleSaveImages = () => {
    setSelectedProduct({ ...selectedProduct, productImagesUrls: imageUrls.filter(url => url !== '') });
    handleCloseImageModal();
    setFeedbackMessage({ text: 'Product images updated successfully!', type: 'success' });
    setTimeout(() => setFeedbackMessage({ text: '', type: '' }), 3000);
  };

  const handleEditClick = (productId) => {
    const productToEdit = rows.find((product) => product.id === productId);
    setSelectedProduct(productToEdit);
    setImageUrls(productToEdit.productImagesUrls);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleSaveProduct = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get("adminKey");
      const formattedProduct = {
        ...selectedProduct,
        productId: selectedProduct.id,
        labels: selectedProduct.labels.split(",").map(label => label.trim()),
        productImagesUrls: selectedProduct.productImagesUrls.filter(url => url.trim() !== ''),
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}products/updateProduct`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Admin-Token": adminToken,
        },
        body: JSON.stringify(formattedProduct),
      });

      if (!response.ok) {
        throw new Error("Failed to save product changes");
      }

      const updatedRows = rows.map((product) =>
        product.id === selectedProduct.id ? selectedProduct : product
      );
      setRows(updatedRows);
      setFilteredRows(prevFiltered => 
        prevFiltered.map(product => 
          product.id === selectedProduct.id ? selectedProduct : product
        )
      );
      
      setFeedbackMessage({ text: 'Product updated successfully!', type: 'success' });
      setTimeout(() => setFeedbackMessage({ text: '', type: '' }), 3000);
      
      setSaveLoading(false);
      handleCloseModal();
    } catch (error) {
      console.error("Error saving product:", error);
      setFeedbackMessage({ text: 'Error saving product. Please try again.', type: 'error' });
      setTimeout(() => setFeedbackMessage({ text: '', type: '' }), 3000);
      setSaveLoading(false);
    }
  };

  const handleCreateProductClick = () => {
    setShowCreateProductModal(true);
  };

  const handleCloseCreateProductModal = () => {
    setShowCreateProductModal(false);
    setNewProduct({
      productId: "",
      productName: "",
      productDescription: "",
      productExtraData: "",
      imageUrl: "",
      status: "Active",
      sku: 0,
      productPrice: 0,
      width: 0,
      height: 0,
      depth: 0,
      quantityAvailable: 0,
      labels: "",
      productImagesUrls: [],
    });
  };

  const handleCreateProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prevProduct) => ({
      ...prevProduct,
      [name]: value,
    }));
  };

  const handleSaveNewProduct = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get("adminKey");
      const formattedProduct = {
        ...newProduct,
        labels: newProduct.labels.split(",").map((label) => label.trim()),
        productImagesUrls: newProduct.productImagesUrls.filter((url) => url.trim() !== ''),
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}products/createProduct`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Admin-Token": adminToken,
        },
        body: JSON.stringify(formattedProduct),
      });

      if (!response.ok) {
        throw new Error("Failed to create new product");
      }

      const newProductData = await response.json();
      const formattedNewProduct = {
        id: newProductData.productId,
        ...newProductData,
        labels: newProductData.labels.join(", "),
      };
      
      setRows((prevRows) => [...prevRows, formattedNewProduct]);
      setFilteredRows((prevFiltered) => [...prevFiltered, formattedNewProduct]);
      
      setFeedbackMessage({ text: 'Product created successfully!', type: 'success' });
      setTimeout(() => setFeedbackMessage({ text: '', type: '' }), 3000);
      
      setSaveLoading(false);
      handleCloseCreateProductModal();
    } catch (error) {
      console.error("Error creating product:", error);
      setFeedbackMessage({ text: 'Error creating product. Please try again.', type: 'error' });
      setTimeout(() => setFeedbackMessage({ text: '', type: '' }), 3000);
      setSaveLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Active': 'bg-green-100 text-green-800 border-green-200',
      'Inactive': 'bg-red-100 text-red-800 border-red-200',
      'Featured': 'bg-blue-100 text-blue-800 border-blue-200',
      'default': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return (
      <div className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${statusStyles[status] || statusStyles.default} border`}>
        {status === 'Active' && <FiCheck className="inline mr-1" />}
        {status === 'Inactive' && <FiX className="inline mr-1" />}
        {status === 'Featured' && <FiTag className="inline mr-1" />}
        <span className="capitalize">{status}</span>
      </div>
    );
  };

  const columns = [
    { 
      field: "id", 
      headerName: "ID", 
      width: 150,
      renderCell: (params) => (
        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded-lg text-gray-700">
          {params.value}
        </span>
      )
    },
    { 
      field: "productName", 
      headerName: "Product", 
      width: 240,
      renderCell: (params) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-md bg-gray-100 overflow-hidden flex-shrink-0 mr-3">
            <img 
              src={params.row.imageUrl.startsWith('http') ? params.row.imageUrl : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${params.row.imageUrl}`} 
              alt={params.row.productName} 
              className="h-full w-full object-cover"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/40'; }}
            />
          </div>
          <div>
            <div className="font-medium text-gray-900">{params.value}</div>
            <div className="text-xs text-gray-500 truncate max-w-[150px]">SKU: {params.row.sku.toString().padStart(5, '0')}</div>
          </div>
        </div>
      )
    },
    { 
      field: "productCategory", 
      headerName: "Category", 
      width: 120,
      renderCell: (params) => (
        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">
          {params.value}
        </span>
      )
    },
    { 
      field: "status", 
      headerName: "Status", 
      width: 120,
      renderCell: (params) => {
        return (
          <div className="mt-3">
            {getStatusBadge(params.value)}
          </div>
        );
      }
    },
    { 
      field: "productPrice", 
      headerName: "Price", 
      width: 100,
      renderCell: (params) => (
        <div className="font-semibold text-gray-900">€{params.value.toFixed(2)}</div>
      )
    },
    { 
      field: "dimensions", 
      headerName: "Dimensioni", 
      width: 130,
      renderCell: (params) => {
        if (!params.row.width && !params.row.height && !params.row.depth) {
          return "-";
        }
        return `${params.row.width || 0}x${params.row.height || 0}x${params.row.depth || 0}cm`;
      }
    },
    { 
      field: "quantityAvailable", 
      headerName: "Stock", 
      width: 90,
      renderCell: (params) => {
        const stockLevel = params.value;
        let stockClass = "text-green-700";
        
        if (stockLevel <= 0) {
          stockClass = "text-red-700 font-bold";
        } else if (stockLevel <= 5) {
          stockClass = "text-amber-700 font-bold";
        }
        
        return (
          <div className={stockClass}>
            {stockLevel}
          </div>
        );
      }
    },
    { 
      field: "labels", 
      headerName: "Labels", 
      width: 200,
      renderCell: (params) => {
        if (!params.value) return "-";
        
        const labels = params.value.split(',').map(label => label.trim());
        return (
          <div className="flex flex-wrap gap-1 py-1 mt-3">
            {labels.slice(0, 2).map((label, index) => (
              <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                {label}
              </span>
            ))}
            {labels.length > 2 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                +{labels.length - 2}
              </span>
            )}
          </div>
        );
      }
    },
    { 
      field: "actions", 
      headerName: "Actions", 
      width: 160,
      renderCell: (params) => (
        <div className="flex space-x-2 mt-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center px-3 py-1.5 bg-[#406580] text-white rounded-md text-sm font-medium hover:bg-[#305570] transition-colors"
            onClick={() => handleEditClick(params.row.id)}
          >
            <FiEdit className="mr-1" size={14} />
            Edit
          </motion.button>
          
          <motion.a 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href={`/product/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors"
          >
            <FiLink className="mr-1" size={14} />
            View
          </motion.a>
        </div>
      )
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-t-4 border-b-4 border-[#406580] rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Caricamento prodotti...</p>
        </div>
      </div>
    );
  }

    return (
    <motion.section 
      initial="hidden" 
      animate="visible" 
      variants={containerVariants} 
      className="w-full mt-1 max-w-[104rem]"
    >
      <motion.div variants={itemVariants} className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestione Prodotti</h1>
          <p className="text-sm text-gray-600 mt-1">Gestisci e visualizza tutti i prodotti disponibili</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <motion.div variants={itemVariants} className="bg-white rounded-lg border border-gray-200 flex items-center px-3 py-2 shadow-sm">
            <FiSearch className="text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Cerca prodotti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-none focus:ring-0 text-sm outline-none"
            />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="category-filter-label">Categoria</InputLabel>
              <Select
                labelId="category-filter-label"
                value={filterCategory}
                label="Categoria"
                onChange={(e) => setFilterCategory(e.target.value)}
                sx={{ borderRadius: '0.5rem' }}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {[...new Set(rows.map(product => product.productCategory.toLowerCase()))].map((category) => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </motion.div>
          
          <motion.button
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCreateProductClick}
            className="flex items-center px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors"
          >
            <FiPlus className="mr-2" />
            Nuovo Prodotto
          </motion.button>
        </div>
      </motion.div>
      
      <AnimatePresence>
        {feedbackMessage.text && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`mb-4 p-3 rounded-lg text-sm flex items-center ${
              feedbackMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {feedbackMessage.type === 'success' ? (
              <FiCheck className="mr-2" />
            ) : (
              <FiX className="mr-2" />
            )}
            {feedbackMessage.text}
          </motion.div>
        )}
      </AnimatePresence>
  
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Box sx={{ height: 750, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{ field: "productName", sort: "asc" }],
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            checkboxSelection
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f3f4f6',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f9fafb',
              },
            }}
          />
        </Box>
      </motion.div>
  
      {/* Edit Product Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <Modal 
            open={Boolean(selectedProduct)} 
            onClose={handleCloseModal}
            closeAfterTransition
            BackdropComponent={({ children }) => (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseModal}
                className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              >
                {children}
              </motion.div>
            )}
          >
            <div>
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute top-[125px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-[#406580] flex items-center justify-center text-white mr-3">
                      <FiPackage size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">Modifica Prodotto</h2>
                      <p className="text-sm text-gray-500">{selectedProduct.id}</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX size={24} />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <TextField
                    label="Nome Prodotto"
                    fullWidth
                    value={selectedProduct.productName}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, productName: e.target.value })}
                  />
                  <TextField
                    label="Descrizione"
                    fullWidth
                    multiline
                    rows={2}
                    value={selectedProduct.productDescription}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, productDescription: e.target.value })}
                  />
                  <TextField
                    label="Dati Extra"
                    fullWidth
                    value={selectedProduct.productExtraData}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, productExtraData: e.target.value })}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Stato</InputLabel>
                    <Select
                      value={selectedProduct.status}
                      label="Stato"
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, status: e.target.value })}
                    >
                      <MenuItem value="Active">Attivo</MenuItem>
                      <MenuItem value="Inactive">Inattivo</MenuItem>
                      <MenuItem value="Featured">In Evidenza</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="SKU"
                    fullWidth
                    type="number"
                    value={selectedProduct.sku}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, sku: parseFloat(e.target.value) })}
                  />
                  <TextField
                    label="Prezzo"
                    fullWidth
                    type="number"
                    value={selectedProduct.productPrice}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, productPrice: parseFloat(e.target.value) })}
                    InputProps={{
                      startAdornment: <span className="text-gray-400 mr-1">€</span>,
                    }}
                  />
                  <div className="flex gap-4">
                    <TextField
                      label="Larghezza"
                      fullWidth
                      type="number"
                      value={selectedProduct.width}
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, width: parseFloat(e.target.value) })}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                    <TextField
                      label="Altezza"
                      fullWidth
                      type="number"
                      value={selectedProduct.height}
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, height: parseFloat(e.target.value) })}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                    <TextField
                      label="Profondità"
                      fullWidth
                      type="number"
                      value={selectedProduct.depth}
                      onChange={(e) => setSelectedProduct({ ...selectedProduct, depth: parseFloat(e.target.value) })}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                  </div>
                  <TextField
                    label="Quantità Disponibile"
                    fullWidth
                    type="number"
                    value={selectedProduct.quantityAvailable}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, quantityAvailable: parseInt(e.target.value, 10) })}
                  />
                  <TextField
                    label="Etichette (separate da virgola)"
                    fullWidth
                    value={selectedProduct.labels}
                    onChange={(e) => setSelectedProduct({ ...selectedProduct, labels: e.target.value })}
                  />
                </div>
  
                {/* Image Preview */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <button 
                      className="text-blue-600 text-sm hover:underline flex items-center"
                      onClick={handleEditImagesClick}
                    >
                      <FiImage className="mr-1" /> Modifica Immagini Aggiuntive
                    </button>
                  </div>
                </div>
  
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSaveProduct}
                    disabled={saveLoading}
                    className="px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                  >
                    {saveLoading ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Salvataggio...</span>
                      </div>
                    ) : (
                      <>
                        <FiSave className="mr-2" />
                        Salva Modifiche
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
  
      {/* Image URLs Modal */}
      <AnimatePresence>
        {showImageModal && (
          <Modal 
            open={showImageModal} 
            onClose={handleCloseImageModal}
            closeAfterTransition
            BackdropComponent={({ children }) => (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseImageModal}
                className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              >
                {children}
              </motion.div>
            )}
          >
            <div>
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute top-[125px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                  <h2 className="text-xl font-bold text-gray-800">Immagini Aggiuntive</h2>
                  <button 
                    onClick={handleCloseImageModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX size={20} />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {[...Array(10)].map((_, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center text-gray-400">
                        {index + 1}
                      </div>
                      <TextField
                        label={`URL Immagine ${index + 1}`}
                        fullWidth
                        size="small"
                        value={imageUrls[index] || ''}
                        onChange={(e) => handleImageChange(index, e)}
                        InputProps={{
                          startAdornment: <FiImage className="text-gray-400 mr-2" />,
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCloseImageModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSaveImages}
                    className="px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors flex items-center"
                  >
                    <FiSave className="mr-2" />
                    Salva Immagini
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
  
      {/* Create Product Modal */}
      <AnimatePresence>
        {showCreateProductModal && (
          <Modal 
            open={showCreateProductModal} 
            onClose={handleCloseCreateProductModal}
            closeAfterTransition
            BackdropComponent={({ children }) => (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseCreateProductModal}
                className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              >
                {children}
              </motion.div>
            )}
          >
            <div>
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute top-[125px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-[#406580] flex items-center justify-center text-white mr-3">
                      <FiPlus size={24} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Nuovo Prodotto</h2>
                  </div>
                  <button 
                    onClick={handleCloseCreateProductModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX size={20} />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <TextField
                    label="ID Prodotto"
                    fullWidth
                    name="productId"
                    value={newProduct.productId}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="Nome Prodotto"
                    fullWidth
                    name="productName"
                    value={newProduct.productName}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="Descrizione"
                    fullWidth
                    multiline
                    rows={2}
                    name="productDescription"
                    value={newProduct.productDescription}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="Dati Extra"
                    fullWidth
                    name="productExtraData"
                    value={newProduct.productExtraData}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="URL Immagine"
                    fullWidth
                    name="imageUrl"
                    value={newProduct.imageUrl}
                    onChange={handleCreateProductChange}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Stato</InputLabel>
                    <Select
                      name="status"
                      value={newProduct.status}
                      label="Stato"
                      onChange={handleCreateProductChange}
                    >
                      <MenuItem value="Active">Attivo</MenuItem>
                      <MenuItem value="Inactive">Inattivo</MenuItem>
                      <MenuItem value="Featured">In Evidenza</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="SKU"
                    fullWidth
                    type="number"
                    name="sku"
                    value={newProduct.sku}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="Prezzo"
                    fullWidth
                    type="number"
                    name="productPrice"
                    value={newProduct.productPrice}
                    onChange={handleCreateProductChange}
                    InputProps={{
                      startAdornment: <span className="text-gray-400 mr-1">€</span>,
                    }}
                  />
                  <div className="flex gap-4">
                    <TextField
                      label="Larghezza"
                      fullWidth
                      type="number"
                      name="width"
                      value={newProduct.width}
                      onChange={handleCreateProductChange}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                    <TextField
                      label="Altezza"
                      fullWidth
                      type="number"
                      name="height"
                      value={newProduct.height}
                      onChange={handleCreateProductChange}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                    <TextField
                      label="Profondità"
                      fullWidth
                      type="number"
                      name="depth"
                      value={newProduct.depth}
                      onChange={handleCreateProductChange}
                      InputProps={{
                        endAdornment: <span className="text-gray-400">cm</span>,
                      }}
                    />
                  </div>
                  <TextField
                    label="Quantità Disponibile"
                    fullWidth
                    type="number"
                    name="quantityAvailable"
                    value={newProduct.quantityAvailable}
                    onChange={handleCreateProductChange}
                  />
                  <TextField
                    label="Etichette (separate da virgola)"
                    fullWidth
                    name="labels"
                    value={newProduct.labels}
                    onChange={handleCreateProductChange}
                  />
                </div>
                
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCloseCreateProductModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSaveNewProduct}
                    disabled={saveLoading}
                    className="px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                  >
                    {saveLoading ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Creazione...</span>
                      </div>
                    ) : (
                      <>
                        <FiPlus className="mr-2" />
                        Crea Prodotto
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

const modalBoxStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};